<template>
  <div class="content-box">
    <div class="category-box">
      <div>
        <DropdownStandard
          class="dropdown"
          :disabled="false"
          :hasSearch="true"
          :title="title"
          :searchType="t('general__text__category').toLocaleLowerCase()"
          :label="t('general__text__category')"
          :options="categoryOptions"
          :required="true"
          @select="(id) => emit('selectCategory', id)"
        />
      </div>
      <div>
        <DropdownStandard
          class="dropdown"
          :disabled="!subCategoryOptions.length"
          :hasSearch="true"
          :title="subCategoryTitle"
          :required="true"
          :searchType="t('general__text__sub-category').toLocaleLowerCase()"
          :label="t('general__text__sub-category')"
          :options="subCategoryOptions"
          @select="(id) => emit('selectSubCategory', id)"
        />
      </div>
    </div>
    <div>
      <TextFieldStandard
        class="textfield"
        :hideIconX="true"
        :label="t('general__text__name')"
        :prefilledText="name"
        @emitInput="onNameInput"
      />
    </div>
    <div class="description-box">
      <label class="label" for="description-textarea">{{ t('general__text__description') }}</label>
      <textarea
        id="description-textarea"
        class="textarea"
        :value="description"
        @input="emit('updateDescription', ($event.target as HTMLTextAreaElement).value)"
      ></textarea>
    </div>
    <div class="height">
      <TextFieldStandard
        class="dropdown"
        :infoText="
          isOutdoorPoi
            ? t('info__poi-height-rh2000-realestate')
            : t('info__poi-height-rh2000-level')
        "
        :label="t('general__text__height-in-RH')"
        :hideIconX="true"
        :prefilledText="rh2000String"
        @emitInput="(val) => emit('updateRh2000String', val)"
      />
      <TextFieldStandard
        class="dropdown"
        :label="
          isPlanviewShowing
            ? t('general__text__estimated-height-from-floor-m')
            : t('general__text__estimated-height-from-ground-m')
        "
        :infoText="t('info__poi-estimated-height')"
        :hideIconX="true"
        :prefilledText="
          estimatedHeight || estimatedHeight === 0 ? formatNumber(estimatedHeight, 2) : ''
        "
        @emitInput="(val) => emit('updateEstimatedHeight', val)"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import { formatNumber } from '@/utils/math'
import DropdownStandard, { optionType } from '@/design-system/molecules/DropdownStandard.vue'
import TextFieldStandard from '@/design-system/atoms/TextFieldStandard.vue'

interface Props {
  name: string
  description: string
  rh2000String: string
  estimatedHeight: number | null
  categoryOptions: optionType[]
  subCategoryOptions: optionType[]
  title: string
  subCategoryTitle: string
  isOutdoorPoi: boolean
  isPlanviewShowing: boolean
  editMode: boolean
}
defineProps<Props>()

const emit = defineEmits<{
  (e: 'updateName', value: string): void
  (e: 'updateDescription', value: string): void
  (e: 'updateRh2000String', value: string | number): void
  (e: 'updateEstimatedHeight', value: string | number): void
  (e: 'selectCategory', id: string): void
  (e: 'selectSubCategory', id: string): void
}>()

const { t } = useI18n()

function onNameInput(value: string | number) {
  emit('updateName', String(value))
}
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins.scss';
@use '@/design-system/design-tokens/mixins.scss';

.header {
  @include font-mixins.desktop-bold-h4;
  padding-bottom: variables.$padding-medium;
  color: #1a1a1a;
}

.sub-header {
  @include font-mixins.desktop-bold-h5;
  color: #1a1a1a;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 24px;
  cursor: pointer;
}

.header-text {
  padding-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.category-box {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 16px;
}

.textfield,
.dropdown {
  width: 344px;
}

.horizontal-divider {
  background-color: #e0e0e0;
  height: 1px;
  width: 100%;
}

.label {
  @include font-mixins.desktop-bold-paragraph;
  color: black;
  padding: variables.$padding-small 0;
}

.textarea {
  @include mixins.scrollbar-vertical;
  @include font-mixins.desktop-regular-paragraph;
  width: 672px;
  height: 104px;
  flex-shrink: 0;
  border-radius: 12px;
  border: 1px solid #767676;
}

.description-box {
  display: flex;
  flex-direction: column;
}

.height {
  display: flex;
  flex-direction: row;
  gap: variables.$gap-xlarge;
  padding: variables.$padding-medium 0;
  justify-self: space-between;

  & div {
    width: 344px;
  }
}

.content-box {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.container {
  background-color: #ffffff;
  border-radius: variables.$border-radius-small;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: absolute;
  left: 50%;
  top: 45%;
  transform: translate(-50%, -50%);
  padding: 24px;
  width: 800px;

  h2 {
    margin-top: 2px;
  }

  .category {
    @include font-mixins.desktop-bold-paragraph;
    color: black;
    display: flex;
    flex-direction: row;
    gap: variables.$gap-xlarge;
    padding-bottom: variables.$padding-medium;

    & div {
      width: 100%;
    }
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: variables.$gap-small;
    justify-content: space-evenly;
    padding: variables.$padding-medium 0;

    .button {
      width: 100%;
    }

    .textarea {
      max-width: 100%;
    }
  }

  .footer {
    align-items: flex-end;
    display: flex;
    flex-direction: row;
    gap: variables.$gap-large;
    justify-content: flex-end;
    padding-top: variables.$padding-medium;
  }

  .accordion__arrow-button__icon {
    transition: transform 0.3s ease;
  }

  .rotated {
    transform: rotate(180deg);
  }

  .height {
    @extend .height;
  }

  .label {
    @extend .label;
  }
}

@media screen and (max-width: 768px) {
  .container {
    display: block;
    max-height: 650px;
    @include mixins.scrollbar-vertical;
    overflow-y: auto;
    top: 50%;
    width: 300px;

    .category {
      flex-direction: column;
    }

    .content .textarea {
      max-width: 268px;
    }
  }
}

@media screen and (max-height: 670px) {
  .container {
    max-height: 500px;
  }
}
</style>
